/**
 * SkeletonFallback — Suspense fallback (3단계 Skeleton)
 *
 * - ChatSkeleton: 채팅 영역 스켈레톤
 * - ArtifactSkeleton: Artifact 영역 스켈레톤
 * - MessageListSkeleton: 메시지 목록 스켈레톤
 *
 * 사용:
 *   <Suspense fallback={<ChatSkeleton />}>
 */

import { cn } from "@/shared/lib/utils";

/* ══════════════════════════════════════════════════════════════════════════
   Pulse 기본 블록
   ══════════════════════════════════════════════════════════════════════════ */

function Pulse({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "animate-pulse rounded-lg bg-surface-secondary/70",
        className,
      )}
    />
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   MessageListSkeleton — 메시지 목록
   ══════════════════════════════════════════════════════════════════════════ */

export function MessageListSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("flex-1 space-y-5 px-4 py-4", className)}>
      {/* User message */}
      <div className="flex justify-end">
        <Pulse className="h-10 w-48 rounded-2xl" />
      </div>

      {/* Assistant message */}
      <div className="flex items-start gap-2">
        <Pulse className="size-7 shrink-0 rounded-full" />
        <div className="flex-1 space-y-2">
          <Pulse className="h-4 w-3/4" />
          <Pulse className="h-4 w-1/2" />
          <Pulse className="h-4 w-2/3" />
        </div>
      </div>

      {/* Execution steps skeleton */}
      <div className="ml-9 rounded-lg border border-stroke-subtle p-3 space-y-2">
        <Pulse className="h-3 w-24" />
        <Pulse className="h-3 w-40" />
        <Pulse className="h-3 w-32" />
      </div>

      {/* Widget skeleton */}
      <div className="ml-9">
        <Pulse className="h-36 w-full rounded-xl" />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   ArtifactSkeleton — Artifact 패널
   ══════════════════════════════════════════════════════════════════════════ */

export function ArtifactSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex flex-col border-l border-stroke-subtle p-4 space-y-4",
        "w-full lg:w-[340px] xl:w-[420px]",
        className,
      )}
    >
      <Pulse className="h-5 w-32" />
      <Pulse className="h-40 w-full rounded-xl" />
      <div className="space-y-2">
        <Pulse className="h-3 w-full" />
        <Pulse className="h-3 w-5/6" />
        <Pulse className="h-3 w-4/6" />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   ChatSkeleton — 채팅 영역 전체
   ══════════════════════════════════════════════════════════════════════════ */

export function ChatSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("flex flex-1 flex-col overflow-hidden", className)}>
      {/* Header */}
      <div className="flex h-14 items-center gap-3 border-b border-stroke-subtle px-4">
        <Pulse className="h-8 w-36 rounded-lg" />
        <div className="flex-1" />
        <Pulse className="size-8 rounded-lg" />
        <Pulse className="size-8 rounded-lg" />
      </div>

      {/* Messages */}
      <MessageListSkeleton className="flex-1" />

      {/* Input */}
      <div className="border-t border-stroke-subtle p-4">
        <Pulse className="h-12 w-full rounded-xl" />
      </div>
    </div>
  );
}
